import { useEffect, useState, useRef, useContext } from 'react';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getImplantsByTerm } from '../../services/apiService';
import Search from './Search';
import styles from './ResultsComponent.module.css';
import LoadingRing from '../ui-components/LoadingRing';
import ReportMissingImplant from './ReportMissingImplant';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleXmark } from '@fortawesome/free-regular-svg-icons';
import SETTING_KEYS from '../../constants/setting-keys';
import SortIcon from '../ui-components/SortIcon';
import { mriCodeValidate } from '../../services/dataValidation';
import { resolveActiveSystemByMriCode } from '../../services/api/activeImplantsService';
import { isActiveImplantMriCode } from '@nordinsight/implant-core';
import AuthContext from '../../store/auth-context';
import React from 'react';
import RIGHTS_KEYS from '../../constants/rights-keys';
import DatapipeLink from '../general/DatapipeLink';
import { getDatapipeImplantEditUrl } from '../../utils/datapipeLinks';
import NewImplantBadge from '../implant/new-implant-badge/NewImplantBadge';
import Status from '../general-components/Status';
import ActiveFilters from './ActiveFilters';
import { onlyUnique, toTitleCase } from '../../utils/utils';
import { getFlagsFromRegions } from '../../utils/regionFlags';
import {
    EVENT_KEYS,
    trackClick,
    trackLoad,
} from '../../services/api/trackingService';
import { FilterSearchSheet } from '../ui/sheet/FilterSearchSheet';
import { FilterSearchDrawer } from '../ui/drawer/FilterSearchDrawer';
import CardComponent from '../general/Cards/CardComponent';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { Tooltip } from '@mui/material';
import GppGoodIcon from '@mui/icons-material/GppGood';
import ReportMissingImplantButton from './ReportMissingImplantButton';

const ResultsComponent: React.FC<{
    term: string;
    defaultSemantic: boolean;
    isFullview?: boolean;
}> = ({ term, defaultSemantic, isFullview = true }) => {
    const { t } = useTranslation();
    const [results, setResults] = useState<any[]>([]);
    const [resultsLoading, setResultsLoading] = useState(false);
    const [showReportModal, setShowReportModal] = useState(false);
    const [skipAmount, setSkipAmount] = useState(0);
    const [sortStatus, setSortStatus] = useState(['none', '']);
    const resultCompRef = useRef<HTMLDivElement>();
    const [path, setPath] = useState('');
    const [showImplantPage, setShowImplantPage] = useState(false);
    const [suggestedText, setSuggestedText] = useState('');

    const [currentTerm, setCurrentTerm] = useState(term);
    const [useSemantic, setUseSemantic] = useState(defaultSemantic);
    const matches = useMediaQuery('(min-width: 768px)');

    const userContext = useContext(AuthContext);

    const [activeFilters, setActiveFilters] = useState<{
        [key: string]: string[];
    }>({ Types: [] });

    const sort = (event: React.MouseEvent) => {
        setSkipAmount(0);
        let parent = event.target as HTMLElement;
        while (parent.tagName !== 'TH') {
            parent = parent.parentElement as HTMLElement;
        }
        const v = parent.id;

        if (sortStatus[0] === v) {
            if (sortStatus[1] === '') {
                setSortStatus([v, 'asc']);
            } else if (sortStatus[1] === 'asc') {
                setSortStatus([v, 'desc']);
            } else if (sortStatus[1] === 'desc') {
                setSortStatus(['none', '']);
            }
        } else {
            setSortStatus([v, 'asc']);
        }
    };

    const search = async (
        sTerm: string,
        s: boolean,
        filters?: { [key: string]: string[] }
    ) => {
        setCurrentTerm(sTerm);
        setUseSemantic(s);
        let path = '/search/query?term=' + sTerm;
        if (s) {
            path += '&useSemantic=true';
        }
        // if (filters) {
        //     path += `&filters=${JSON.stringify(filters)}`;
        // }
        if (isFullview) {
            window.history.replaceState(null, 'NordRadiology', path);
        }

        setResultsLoading(true);
        const implants = await getImplants(sTerm, 0, s, filters);
        setSkipAmount(100);
        setResults(implants);
        setResultsLoading(false);
        trackLoad(EVENT_KEYS.searchResults, {
            searchTerm: sTerm,
            resultCount: implants.length,
        });
    };

    const searchOnScroll = async (sTerm: string, skip: number) => {
        const implants = await getImplants(sTerm, skip, useSemantic);
        setResults((prev) => prev.concat(implants));
    };

    const getImplants = async (
        sTerm: string,
        skip: number,
        semantic: boolean,
        filters?: { [key: string]: string[] }
    ) => {
        setSuggestedText(null);
        const result = await getImplantsByTerm(
            sTerm,
            skip,
            sortStatus[0],
            sortStatus[1],
            semantic,
            filters
        );
        setSuggestedText(result.newTerm);

        let arr = [];
        if (Array.isArray(result.implants)) {
            arr = result.implants;
        } else if (typeof result.implants === typeof '') {
            arr = new Array(result.implants);
        }
        setResultsLoading(false);
        return arr;
    };

    useEffect(() => {
        const pageComp = resultCompRef.current.parentElement;
        const handleScroll = () => {
            if (
                resultCompRef.current.getBoundingClientRect().height <
                    pageComp.scrollTop +
                        document.documentElement.clientHeight &&
                results.length % 100 === 0
            ) {
                searchOnScroll(currentTerm, skipAmount);
                setSkipAmount((prev) => prev + 100);
                pageComp.removeEventListener('scroll', handleScroll);
            }
        };
        pageComp.addEventListener('scroll', handleScroll);

        return () => {
            pageComp.removeEventListener('scroll', handleScroll);
        };
    }, [results]);

    useEffect(() => {
        setResultsLoading(false);
        if (sortStatus[0] !== 'none') {
            (document.getElementById(sortStatus[0]) as HTMLElement).style[
                'fontWeight'
            ] = 'bold';
        }
    }, [results]);

    const mriCodeRedirect = async (term: string) => {
        const trimmed = term.trim();
        // Active-implant codes (`AB-A-1`) open the picker deep link; the
        // gate mirrors the /active-implants route mount so we never
        // navigate somewhere that isn't rendered for this user.
        const canOpenActiveImplant =
            userContext.orRights([RIGHTS_KEYS.manageAllImplants]) &&
            userContext.orRights([RIGHTS_KEYS.advancedSearch]);
        if (isActiveImplantMriCode(trimmed) && canOpenActiveImplant) {
            const resolved = await resolveActiveSystemByMriCode(trimmed);
            if (resolved) {
                setPath('/active-implants/' + resolved.mriCode);
                setShowImplantPage(true);
                return;
            }
        }

        const validMriCode = await mriCodeValidate(term);
        if (
            validMriCode &&
            userContext.orRights([RIGHTS_KEYS.advancedSearch])
        ) {
            const pathToImplantId = '/implant/' + term;
            setPath(pathToImplantId);
            setShowImplantPage(true);
        } else {
            setShowImplantPage(false);
        }
    };

    useEffect(() => {
        const sTerm = currentTerm === '' || !isFullview ? term : currentTerm;

        mriCodeRedirect(sTerm);

        search(sTerm, useSemantic);
    }, [term, sortStatus]);

    return (
        <div className={styles.wrapper} ref={resultCompRef}>
            {showReportModal && (
                <ReportMissingImplant
                    searchTerm={currentTerm}
                    onClose={() => setShowReportModal(false)}
                />
            )}
            {isFullview && (
                <div className={styles.search}>
                    <div className="flex flex-row gap-4 items-start justify-between">
                        <Search
                            defaultSemantic={useSemantic}
                            defaultValue={term}
                            hasQuickResult={false}
                            onSearch={(
                                term: string,
                                semanticEnabled: boolean
                            ) => search(term, semanticEnabled)}
                        />
                        {results.length > 0 && (
                            <ReportMissingImplantButton
                                currentTerm={currentTerm}
                            />
                        )}
                    </div>
                    {suggestedText && (
                        <p className={styles.suggestedText}>
                            {t('search.resultsComponent.suggestedText.message')}{' '}
                            <span>
                                <a href={'/search/query?term=' + suggestedText}>
                                    {suggestedText}
                                </a>
                            </span>{' '}
                            {t('search.resultsComponent.suggestedText.instead')}
                        </p>
                    )}
                </div>
            )}

            {!resultsLoading && results.length === 0 && (
                <div className={styles.loading}>
                    <div className={styles.noresult}>
                        <div className={styles.loupe}>
                            <FontAwesomeIcon icon={faCircleXmark} />
                        </div>
                        <h3>{t('search.resultsComponent.noResults.title')}</h3>
                    </div>
                    <ReportMissingImplantButton currentTerm={currentTerm} />
                </div>
            )}
            {!resultsLoading && showImplantPage && (
                <Navigate to={path} replace={true} />
            )}
            <div className={styles.section}>
                {results.length > 0 && (
                    <>
                        <div className="flex flex-row text-lg justify-between items-center w-full">
                            <div className="flex flex-row gap-4 items-center">
                                <h3>
                                    {t(
                                        'search.resultsComponent.searchResults.title'
                                    )}
                                </h3>
                            </div>
                            {matches ? (
                                <FilterSearchSheet
                                    results={results}
                                    defaultActiveFilters={activeFilters}
                                    setActiveFilters={setActiveFilters}
                                    currentTerm={currentTerm}
                                    search={search}
                                />
                            ) : (
                                <FilterSearchDrawer
                                    results={results}
                                    defaultActiveFilters={activeFilters}
                                    setActiveFilters={setActiveFilters}
                                    currentTerm={currentTerm}
                                    search={search}
                                />
                            )}
                        </div>
                        <ActiveFilters filters={activeFilters} />
                    </>
                )}
                {resultsLoading && (
                    <div className={styles.loading}>
                        <LoadingRing size="large" />
                    </div>
                )}

                {matches ? (
                    <>
                        <div className="md:flex flex-1">
                            {!resultsLoading && results.length > 0 && (
                                <div className="flex-1">
                                    <table className={styles.list}>
                                        <thead>
                                            <tr>
                                                <th
                                                    id={SETTING_KEYS.model}
                                                    onClick={(e) => sort(e)}
                                                >
                                                    {t(
                                                        'search.resultsComponent.table.headers.name'
                                                    )}{' '}
                                                    <SortIcon
                                                        sortStatus={sortStatus}
                                                        headerId={
                                                            SETTING_KEYS.model
                                                        }
                                                    />
                                                </th>
                                                <th
                                                    id={
                                                        SETTING_KEYS.manufacturer
                                                    }
                                                    onClick={(e) => sort(e)}
                                                >
                                                    {t(
                                                        'search.resultsComponent.table.headers.brand'
                                                    )}{' '}
                                                    <SortIcon
                                                        sortStatus={sortStatus}
                                                        headerId={
                                                            SETTING_KEYS.manufacturer
                                                        }
                                                    />
                                                </th>
                                                <th
                                                    id={SETTING_KEYS.type}
                                                    onClick={(e) => sort(e)}
                                                >
                                                    {t(
                                                        'search.resultsComponent.table.headers.type'
                                                    )}{' '}
                                                    <SortIcon
                                                        sortStatus={sortStatus}
                                                        headerId={
                                                            SETTING_KEYS.type
                                                        }
                                                    />
                                                </th>
                                                <th id={SETTING_KEYS.type}>
                                                    {t(
                                                        'search.resultsComponent.table.headers.mriStatus'
                                                    )}{' '}
                                                </th>
                                                <th style={{ minWidth: '0px' }}>
                                                    {t(
                                                        'search.resultsComponent.table.headers.regions'
                                                    )}
                                                </th>
                                                <th id="Verified">
                                                    {t(
                                                        'search.resultsComponent.table.headers.approved'
                                                    )}
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {results.map((imp) => (
                                                <React.Fragment
                                                    key={
                                                        imp.info._id + '_button'
                                                    }
                                                >
                                                    {userContext.orRights([
                                                        RIGHTS_KEYS.manageAllImplants,
                                                    ]) && (
                                                        <tr
                                                            className={
                                                                styles[
                                                                    'edit-button'
                                                                ]
                                                            }
                                                        >
                                                            <td>
                                                                <DatapipeLink
                                                                    href={getDatapipeImplantEditUrl(
                                                                        imp.info._id
                                                                    )}
                                                                    label={t(
                                                                        'search.resultsComponent.buttons.edit'
                                                                    )}
                                                                    variant="ghost"
                                                                />
                                                            </td>
                                                        </tr>
                                                    )}
                                                    <tr
                                                        onClick={() =>
                                                            (window.location.href =
                                                                '/implant/' +
                                                                imp.info._id)
                                                        }
                                                        key={imp.info._id}
                                                    >
                                                        <td>
                                                            <a
                                                                onClick={() => {
                                                                    trackClick(
                                                                        EVENT_KEYS.clickResult,
                                                                        {
                                                                            searchTerm:
                                                                                term,
                                                                            implantModel:
                                                                                imp
                                                                                    .info[
                                                                                    SETTING_KEYS
                                                                                        .model
                                                                                ],
                                                                        }
                                                                    );
                                                                }}
                                                                href={
                                                                    '/implant/' +
                                                                    imp.info._id
                                                                }
                                                            >
                                                                {
                                                                    imp.info[
                                                                        SETTING_KEYS
                                                                            .model
                                                                    ]
                                                                }
                                                                <NewImplantBadge
                                                                    createdAt={
                                                                        imp
                                                                            .info[
                                                                            SETTING_KEYS
                                                                                .createdAt
                                                                        ]
                                                                    }
                                                                />
                                                            </a>
                                                        </td>
                                                        <td>
                                                            {toTitleCase(
                                                                imp[
                                                                    SETTING_KEYS
                                                                        .manufacturer
                                                                ]
                                                            )}
                                                        </td>
                                                        <td>
                                                            {
                                                                imp.info[
                                                                    SETTING_KEYS
                                                                        .type
                                                                ]
                                                            }
                                                        </td>

                                                        <td>
                                                            <Status
                                                                status={
                                                                    imp.info[
                                                                        SETTING_KEYS
                                                                            .mriStatus
                                                                    ]
                                                                }
                                                            />
                                                        </td>
                                                        <td
                                                            style={{
                                                                fontSize:
                                                                    '24px',
                                                            }}
                                                        >
                                                            {getFlagsFromRegions(
                                                                imp.info.regions
                                                            )}
                                                        </td>
                                                        <td>
                                                            {imp.info.approvals?.includes(
                                                                userContext.getContext()
                                                                    .department
                                                                    ._id
                                                            ) ? (
                                                                <Tooltip
                                                                    title={t(
                                                                        'search.resultsComponent.tooltips.approvedImplant',
                                                                        {
                                                                            departmentName:
                                                                                userContext.getContext()
                                                                                    .department
                                                                                    .name,
                                                                        }
                                                                    )}
                                                                >
                                                                    <span
                                                                        className={
                                                                            styles.approved
                                                                        }
                                                                    >
                                                                        <GppGoodIcon />
                                                                    </span>
                                                                </Tooltip>
                                                            ) : (
                                                                '-'
                                                            )}
                                                        </td>
                                                    </tr>
                                                </React.Fragment>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </>
                ) : (
                    <>
                        <div className={`${styles.wrapper2}`}>
                            {!resultsLoading && (
                                <>
                                    {results.map((imp) => (
                                        <div
                                            onClick={() => {
                                                trackClick(
                                                    EVENT_KEYS.clickResult,
                                                    {
                                                        searchTerm: term,
                                                        implantModel:
                                                            imp.info[
                                                                SETTING_KEYS
                                                                    .model
                                                            ],
                                                    }
                                                );
                                            }}
                                        >
                                            <CardComponent
                                                fixedWidth={false}
                                                link={
                                                    '/implant/' + imp.info._id
                                                }
                                                preview={
                                                    <div
                                                        style={{
                                                            transform:
                                                                'translateX(10px)',
                                                        }}
                                                    >
                                                        <Status
                                                            status={
                                                                imp.info[
                                                                    SETTING_KEYS
                                                                        .mriStatus
                                                                ]
                                                            }
                                                        />
                                                    </div>
                                                }
                                                title={
                                                    imp.info[SETTING_KEYS.model]
                                                }
                                                description={
                                                    imp.info[SETTING_KEYS.type]
                                                }
                                            />
                                        </div>
                                    ))}
                                </>
                            )}
                        </div>
                    </>
                )}
                {/* {userContext.orRights([RIGHTS_KEYS.advancedSearch]) &&
                    isFullview &&
                    results.length > 0 && (
                        <div className={styles.filters}>
                            <ResultFilter
                                term={currentTerm}
                                onChange={(filter) => {
                                    setActiveFilters(filter);
                                    search(currentTerm, false, filter);
                                }}
                            />
                        </div>
                    )} */}
            </div>
        </div>
    );
};

export default ResultsComponent;
